import {Observable, Subject, Subscription, take} from 'rxjs';

import {EventEmitter, inject, Injectable, Signal, TemplateRef} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {getColorClass} from '@shared-types';

import {DialogModel, IDialogConfig} from '../../dialog.types';
import {DialogDirective} from '../directives/dialog.directive';

@Injectable({
	providedIn: 'root',
})
export class DialogControlService {
	/* ------------------------------------------  Provided / Services ------------------------------------------ */
	private readonly matDialog: MatDialog = inject(MatDialog);
	/* ------------------------------------------  Types and Constants ------------------------------------------ */

	/* ------------------------------------------  Signals ------------------------------------------ */

	/* ------------------------------------------  Data ------------------------------------------ */
	private readonly dialogs: Map<string, DialogModel> = new Map();

	/* ------------------------------------------  Constructor ------------------------------------------ */
	constructor() {}

	/* ------------------------------------------  Public Methods ------------------------------------------ */
	public registerDialog<D = any>(dialogModel: DialogModel<D>) {
		this.dialogs.set(dialogModel.id, dialogModel);
	}
	public open<D = any>(id: string, config?: IDialogConfig<D>) {
		const dialog = this.dialogs.get(id);
		if (!dialog) {
			console.error(`Dialog with id: ${id} not found`);
			return;
		}

		const directiveRef = dialog.dialogDirective;
		dialog.applyConfigs({
			...(config || {}),
			id: directiveRef.id(),
			panelClass: directiveRef.panelClass(),
			hasBackdrop: directiveRef.hasBackdrop(),
			backdropClass: directiveRef.backdropclass(),
			disableClose: directiveRef.disableclose(),
			height: directiveRef.height() as string,
			width: directiveRef.width() as string,
			maxHeight: directiveRef.maxHeight() as string,
			maxWidth: directiveRef.maxWidth() as string,
			minHeight: directiveRef.minHeight() as string,
			minWidth: directiveRef.minWidth() as string,
			closeOnNavigation: directiveRef.closeonnavigation(),
			direction: directiveRef.direction(),
		});
		const dialogConfig = dialog.dialogConfig;
		// Merge the provided config with the dialog's config
		// Embed the dialog data into the template
		const data = dialogConfig.data;
		if (dialog.contextElement && data) {
			dialog.contextElement.createEmbeddedView({$implicit: data, ...data});
		}

		// Set the dialog's data
		if (dialogConfig.data) {
			dialog.data$.set(dialogConfig.data);
		}

		// Open the dialog
		dialog.matDialogRef = this.matDialog.open(dialog.contextElement, dialogConfig);
		dialog.state$.set('open');
		dialog.dialogDirective.OnOpen.emit(data);

		// Listen for close events for closing by directive (matDialogClose):
		dialog.matDialogRef
			.backdropClick()
			.pipe(take(1))
			.subscribe(() => {
				if (dialogConfig.disableClose) return;
				this.close(id);
			});

		dialog.matDialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(() => {
				dialog.state$.set('close');
				dialog.data$.set(undefined);
				dialog.matDialogRef = null;
			});

		return dialog.matDialogRef;
	}
	public close<D = any>(id: string, closeData?: D): void {
		const dialog = this.dialogs.get(id);
		if (!dialog) return;
		if (dialog.matDialogRef) {
			dialog.matDialogRef.close(closeData);
		}
		dialog.state$.set('close');
		dialog.data$.set(undefined);
		dialog.matDialogRef = null;
		dialog.dialogDirective.OnClosed.emit(closeData);
	}

	public closeAll() {}

	public getDialogData<D = any>(id: string, getSignal: boolean = false): typeof getSignal extends true ? Signal<D> : D {
		const dialog = this.dialogs.get(id);
		if (!dialog) return null;
		return getSignal ? dialog.data$ : dialog.data$();
	}

	public hasDialog(id: string): boolean {
		return this.dialogs.has(id);
	}

	/* ------------------------------------------  Private Methods ------------------------------------------ */
}
